// React
import React, { useRef } from 'react';
// External
import {
    Button, Modal
} from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
// Internal
import PRODUCT_STEPS from 'constants/productSteps';
import ProductDescription from 'components/shared/molecules/ProductDescription/ProductDescription';
import { updateBooking, updateEngraving } from 'stores/actions';
import CustomiseText from 'components/engraving/CustomiseText/CustomiseText';
import EngravingOptions from 'components/engraving/EngravingOptions/EngravingOptions';
import ImageOptions from 'components/engraving/ImageOptions/ImageOptions';
import FinalApproval from 'components/engraving/FinalApproval/FinalApproval';
// Styles
import './styles.scss';

/**
 * @param {Object} props.show - Style to applied to the component, this overwrites the height and width props
 * @param {Function} props.onHide - Path or Route object to navigate to
 * @param {Function} props.onAddClick - Function to handle extra actions when the user click add
 * @param {boolean} props.divider - Boolean to display the divider between header and body, default is true.
 */
class ProductModal extends React.Component {
    constructor(props) {
        super(props);
        // nextSteps is an array of strings that contains the next steps in the process
        // the functionality is first in, first out
        // e.g ['step 3', 'step 4', 'step 5']
        // the next step in the example is 'step 1'
        const nextSteps = Object.values(PRODUCT_STEPS);
        // steps is an array of strings that contains the previous steps and the current step
        // the array works as a stack
        // The current step in the example below is 'step 2'
        // e.g ['step 1', 'step 2']
        const steps = [nextSteps.shift()];
        this.state = {
            nextSteps,
            steps
        };
        this.setSteps();
    }

    componentDidUpdate(prevProps) {
        // Detects when the modal is shows to rest the steps to be displayed, starting with the description
        if (
            ((!prevProps.show && this.props.show && this.props.engraving.productId)
            || (prevProps.show && !this.props.show && this.props.engraving.productId))
            || (!prevProps.engraving.productId && this.props.engraving.productId)
        ) {
            this.setSteps();
        }
    }

    onNext() {
        // Verify that the engraving has data, otherwise the modal is closed
        // The engraving will only have data if the engraving is checked in the first step (description)
        // if (!this.props.engraving.bookingId) {
            this.props.onHide();
            return;
        // }
        // Updates the step data
        // this.setState((prevState) => {
        //     const { nextSteps } = prevState;
        //     const { steps } = prevState;
        //     if (nextSteps.length === 0) {
        //         this.props.onHide();
        //         return null;
        //     }
        //     steps.push(nextSteps.shift());
        //     return {
        //         nextSteps,
        //         steps
        //     };
        // });
    }

    onPrev() {
        // Updates the step data
        this.setState((prevState) => {
            const { nextSteps } = prevState;
            const { steps } = prevState;
            // When length is 1 it means that the modal is in the second step
            // and it can't go back to the description step
            if (steps.length === 0) {
                return null;
            }
            nextSteps.splice(0, 0, steps.pop());
            return {
                nextSteps,
                steps
            };
        });
    }

    /**
     * Calculates the steps to display in the engraving process depending on the option availability of
     * photo or text engraving. The function retrieves the engraving information from the local config file
     * and depending on the value of the field "PhotoSize" or "Dimensions" the steps are enabled or disabled
     */
    setSteps() {
        if (!this.props.engraving.productId) {
            return;
        }
        let steps = Object.values(PRODUCT_STEPS);
        fetch('../fpc_config.json')
            .then((r) => r.json())
            .then((data) => {
                const selectedConfig = data.Config_File.find((x) => x.SugarID === this.props.engraving.productId);
                if (!selectedConfig) {
                    return;
                }
                // Remove image selection step if engraving doesn't not have photo options
                if (selectedConfig.PhotoSize === '0') {
                    steps = steps.filter((step) => step !== PRODUCT_STEPS.imageOptions);
                }
                // Remove customise text step if engraving doesn't not have plaque options
                if (selectedConfig.Dimensions === '0') {
                    steps = steps.filter(
                        (step) => step !== PRODUCT_STEPS.customiseText || step !== PRODUCT_STEPS.iconSelection
                    );
                }

                this.setState((prevState) => {
                    const newSteps = [...prevState.steps];
                    prevState.steps.forEach((step, index) => {
                        const stepIndex = steps.findIndex((item) => item === step);
                        if (stepIndex >= 0) {
                            // Removes the steps visited or current from the new steps array
                            steps.splice(stepIndex, 1);
                        } else {
                            // Removes the visited and current steps that are not in the new steps
                            newSteps.splice(index, 1);
                            // When the current (step in last position) is removed, the folloring on the steps is pushed
                            if (index === prevState.steps.length - 1) {
                                newSteps.push(steps.shift());
                            }
                        }
                    });
                    return {
                        nextSteps: steps,
                        steps: newSteps
                    };
                });
            }).catch(() => {});
    }

    getCurrentStep() {
        return this.state.steps[this.state.steps.length - 1];
    }

    getNextStep() {
        // Checks that there's a next length and returns it
        return this.state.nextSteps.length > 0 ? this.state.nextSteps[0] : '';
    }

    getPrevStep() {
        // Checks that there's a previous length and returns it
        return this.state.steps.length > 1 ? this.state.steps[this.state.steps.length - 2] : '';
    }

    render() {
        return (
            <Modal className="product-modal" show={this.props.show} onHide={this.props.onHide} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{this.props.product ? this.props.product.name : ''}</Modal.Title>
                </Modal.Header>

                <DisplayCurrentStep
                    {...this.props}
                    prevStep={this.getPrevStep()}
                    currentStep={this.getCurrentStep()}
                    nextStep={this.getNextStep()}
                    onNext={() => this.onNext()}
                    onPrev={() => this.onPrev()}
                />
            </Modal>
        );
    }
}

const DisplayCurrentStep = (props) => {
    const formComponent = useRef(null);

    if (!props.product) {
        return null;
    }

    // Saves the data of the form and sets the next form
    const onNextStep = async () => {
        if (!formComponent || !formComponent.current) {
            return null;
        }
        const formData = await formComponent.current.handleSubmit();
        if (formData) {
            props.updateEngraving(formData);
            props.onNext();
            return formData;
        }
        return null;
    };

    const ContentModal = ({ children }) => (
        <>
            <Modal.Body>
                {children}
            </Modal.Body>

            <Modal.Footer>
                <Button
                    className="btn-back"
                    onClick={() => props.onPrev()}
                >
                    {`Back to ${props.prevStep.toLowerCase()}`}
                </Button>

                <Button
                    variant="primary"
                    onClick={() => onNextStep()}
                >
                    {props.nextStep === '' ? `Finish and add to cart` : `Next`}
                </Button>
            </Modal.Footer>
        </>
    );
    switch (props.currentStep) {
        case PRODUCT_STEPS.description:
            return (
                <ProductDescription
                    product={props.product}
                    isPackage={props.isPackage}
                    onSubmitted={props.onNext}
                    isEdit={props.isEdit}
                />
            );
        case PRODUCT_STEPS.customiseText:
            return (
                <ContentModal>
                    <CustomiseText
                        wrappedComponentRef={formComponent}
                        product={props.product}
                    />
                </ContentModal>
            );
        case PRODUCT_STEPS.iconSelection:
            return (
                <ContentModal>
                    <EngravingOptions
                        wrappedComponentRef={formComponent}
                        product={props.product}
                    />
                </ContentModal>
            );
        case PRODUCT_STEPS.imageOptions:
            return (
                <ContentModal>
                    <ImageOptions
                        wrappedComponentRef={formComponent}
                        product={props.product}
                    />
                </ContentModal>
            );
        case PRODUCT_STEPS.approval:
            return (
                <ContentModal>
                    <FinalApproval
                        wrappedComponentRef={formComponent}
                        product={props.product}
                    />
                </ContentModal>
            );

        default:
            return null;
    }
};

const mapStateToProps = (state) => ({
    booking: state.booking,
    engraving: state.engraving
});

const mapDispatchToProps = (dispatch) => {
    return {
        updateBooking: (bookingObj) => dispatch(updateBooking(bookingObj)),
        updateEngraving: (engravingObj) => dispatch(updateEngraving(engravingObj))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ProductModal));
